import { Component, Inject, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef, Optional } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { AppDataService, ProductService, StoreService } from 'app/services';
import { MessageService } from 'primeng/api';
import { ProductItems } from '../../../../../models/product/get-product-request';
import { PricingTabComponent } from './pricing-tab/pricing-tab.component';
import { UomTabComponent } from './uom-tab/uom-tab.component';
import { AvailabilityTabComponent } from './availability-tab/availability-tab.component';
import { MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'edit-product-dialog',
  templateUrl: './edit-product-dialog.component.html',
  styleUrls: ['./edit-product-dialog.component.scss'],
  providers: [MessageService],
})
export class EditProductDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('stepper', { static: false }) stepper!: MatStepper;
  @ViewChild(PricingTabComponent) pricingTabComponent!: PricingTabComponent;
  @ViewChild(UomTabComponent) uomTabComponent!: UomTabComponent;
  @ViewChild(AvailabilityTabComponent) availabilityTabComponent!: AvailabilityTabComponent;
  product: ProductItems | null = null;
  editedProduct: ProductItems = {} as ProductItems;
  loading = false;
  isStepperReady = false;
  submitted = false;
  productDetailFormGroup!: FormGroup;
  pricingFormGroup!: FormGroup;
  uomFormGroup!: FormGroup;
  availabilityControl = new FormControl('');
  public productCategories: any[] = [];
  public subProductCategories: any[] = [];

  storeSelectedSet = new Set<any>();
  allStores: any[] = [];
  storeSelectedTemp: any;

  allTags: any[] = [];
  selectedTags: any[] = [];
  newTag = '';
  filteredTags: string[] = [];
  public categories: any[] = [];
  public selectedTag: any;

  constructor(
    private formBuilder: FormBuilder,
    private appData: AppDataService,
    private messageService: MessageService,
    private productService: ProductService,
    private storeService: StoreService,
    @Optional() private dialogRef: MatDialogRef<EditProductDialogComponent>,
    private cd: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) @Optional() public data: { product: ProductItems }
  ) {}

  ngOnInit(): void {
    // set product early if passed in
    if (this.data?.product) {
      this.product = this.data.product;
      this.editedProduct = { ...this.product };
      this.productSku = this.product?.Id?.toString() || '';
    }

    this.initializeForms();

    this.loadCategories();

    // If you want to load tags & stores as before:
    this.loadTags();
    this.loadStores();

    // now populate with existing product values
    this.populateForms();

    this.productDetailFormGroup.get('Name')?.valueChanges.subscribe(value => {
    this.editedProduct.Name = value;
    });
    this.productDetailFormGroup.get('Description')?.valueChanges.subscribe(value => {
    this.editedProduct.Description = value;
    });
  }

  ngAfterViewInit(): void {
    this.isStepperReady = true;
    this.cd.detectChanges();
  }

  // compareWith used by mat-select to match objects
  compareById(o1: any, o2: any): boolean {
    if (!o1 && !o2) {
      return true;
    }
    if (!o1 || !o2) {
      return false;
    }
    return o1.Id === o2.Id;
  }

  initializeForms(): void {
    // Use formBuilder and guard using this.product (may be null)
    this.productDetailFormGroup = this.formBuilder.group({
    Name: [this.product?.Name || '', [Validators.required, Validators.maxLength(100)]],
    Category: [null],  // will set later
    Subcategory: [null],
    SKU: [this.product?.Id?.toString() || ''],
    Tags: [this.selectedTags || []],
    Description: [this.product?.Description || '', [Validators.required, Validators.maxLength(500)]],
    ImageUrl: ['', Validators.required],
  });

    this.pricingFormGroup = this.formBuilder.group({
      CostPrice: [0],
      SellingPrice: [''],
      VatAmount: [0],
      DisplayedPrice: [0],
      IsVatable: [false],
    });

    this.uomFormGroup = this.formBuilder.group({
      UOM: ['', Validators.required],
      Measurement: ['', Validators.required],
      Units: ['', Validators.required],
    });
    // If product already has a Category, fill subProductCategories from it
    if (this.product?.CategoryId) {
      const cat = this.getCategoryById(this.product.CategoryId);
      if (cat?.Subcategories) {
        this.subProductCategories = cat.Subcategories;
      }
    }
  }

  private getCategoryById(id: number) {
  if (id == null) {
    return null;
  }
  return this.productCategories.find(c => c.Id === id) || null;
  }

  getSubcategoryById(id?: number) {
    if (id == null) {
      return null;
    }
    return this.subProductCategories.find(s => s.Id === id) || null;
  }
  // ----- API loaders (optional) -----
  loadTags(): void {
    this.productService.getTags(null, this.messageService)
      .then(data => {
        if (data && data.Success) {
          this.allTags = data.Items;
        } else {
          this.allTags = [];
        }
      })
      .catch(() => this.allTags = []);
  }

   initializeSelectedTags(): void {
    // For now, we'll use empty array since ProductItems doesn't have Tags property
    // In a real implementation, you would get tags from the product data
    this.selectedTags = [];
  }

  loadCategories(): void {
  const dto = { ParentId: null, GetAll: true, MerchantId: null };
  this.productService.getCategories(dto, this.messageService)
    .then((data) => {
      if (data && Array.isArray(data) && data.length) {
        this.productCategories = data;
        const catControl = this.productDetailFormGroup.get('Category');

        if (this.product?.CategoryId) {
          const selCat = this.getCategoryById(this.product.CategoryId);
          catControl?.setValue(selCat);
          this.subProductCategories = selCat?.Subcategories || [];
        }
      }
    })
    .catch((err) => {
      console.error('Error loading categories:', err);
    });
  }


  loadStores(): void {
    const dto = {
      MerchantId: this.appData.authData.MerchantId,
    };

    this.storeService
      .getStoresByMerchant(dto, this.messageService)
      .then((data) => {
        if (!data.Success) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: data.ErrorMessage,
          });
          return;
        }
        this.allStores = data.Items;
      })
      .catch((err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err,
        });
      });
  }

  populateForms(): void {
    if (!this.product) {
        return;
    }
    this.productDetailFormGroup.patchValue({
      Name: this.product.Name || '',
      Description: this.product.Description || '',
      ImageUrl: this.product.ImageUrl || '',
      SKU: this.productSku || '',
    });
    // If category object exists, ensure the form is populated and subcategories set
    const selCat = this.getCategoryById(this.product.CategoryId);

    if (selCat) {
      this.productDetailFormGroup.get('Category')?.setValue(selCat);
      this.subProductCategories = selCat.Subcategories || [];
      const selSub = this.getSubcategoryById(this.product.CategoryId);

      if (selSub) {
        this.productDetailFormGroup.get('Subcategory')?.setValue(selSub);
      }
    }


    this.pricingFormGroup.patchValue({
      CostPrice: this.product.DefaultAmount || 0,
      SellingPrice: this.product.PublishedPrice || 0,
      DisplayedPrice: this.product.PublishedPrice || 0,
      VatAmount: this.product.Vat || 0
    });

    this.uomFormGroup.patchValue({
      UOM: this.product.UOM || '',
      Measurement: this.product.UnitText || '',
      Units: this.product.Units || 0,
    });

    if (this.product.ExcludedStores) {
      this.storeSelectedSet = new Set(this.product.ExcludedStores);
    }

    this.selectedTags = this.product.ProductTags;
  }

    onNameChange(value: string): void {
      this.editedProduct.Name = value;

      const control = this.productDetailFormGroup.get('Name');
      if (control) {
        control.setValue(value, { emitEvent: false });
      }
    }
    onCategorySelectionChange(event: MatSelectChange): void {
    const selectedCategory = event.value;
    if (selectedCategory?.Subcategories) {
      this.subProductCategories = selectedCategory.Subcategories;
      this.productDetailFormGroup.get('Subcategory')?.reset();
    } else {
      // fallback: load from API if needed
      this.getSubCategoryFromApi(selectedCategory);
    }
  }

  private getSubCategory(selectedCategory: any): void {
    if (!selectedCategory) {
      this.subProductCategories = [];
      return;
    }

    const dto = {
      ParentId: selectedCategory.Id,
      GetAll: true,
      MerchantId: null,
    };
    this.productService.getCategories(dto, this.messageService).then((data) => {
      if (!data) {
        return;
      }
      if (data.length === 0) {
        this.productDetailFormGroup.get('Subcategory')?.disable();
      }
      this.subProductCategories = data;
    });
  }

  private setCategoryAndSubcategory(): void {
    if (!this.product || !this.productCategories.length) {
      return;
    }

    // Find and set category
    if (this.product.ParentCategoryName) {
      const selectedCategory = this.productCategories.find(cat => cat.Name === this.product?.ParentCategoryName);
      if (selectedCategory) {
        this.productDetailFormGroup.get('Category')?.setValue(selectedCategory);
        this.getSubCategory(selectedCategory);
        // Set subcategory after a short delay to allow subcategories to load
        setTimeout(() => {
          if (this.product?.CategoryName) {
            const selectedSubCategory = this.subProductCategories.find(sub => sub.Name === this.product?.CategoryName);
            if (selectedSubCategory) {
              this.productDetailFormGroup.get('Subcategory')?.setValue(selectedSubCategory);
            }
          }
        }, 100);
      }
    }
  }

  // Store availability methods
  addStoreSelected(store: any): void {
    this.storeSelectedSet.add(this.storeSelectedTemp);
    this.availabilityControl.setValue('');
  }

  removeExcludeStore(store: any): void {
    this.storeSelectedSet.delete(store);
  }

  onDescriptionChange(value: string): void {
  this.editedProduct.Description = value;
  const control = this.productDetailFormGroup.get('Description');
    if (control) {
      control.setValue(value, { emitEvent: false });
    }
  }
  onSkuChange(event: any): void {
    const value = event.target ? event.target.value : event;
    this.productSku = value;
  }

  onNewTagChange(value: string): void {
    this.newTag = value;
  }

  onImageFileSelected(event: any): void {
  const files = event?.target?.files as FileList;
  if (!files || files.length === 0) {
    return;
  }
  const file = files[0];
  if (!file.type.startsWith('image/')) {
    this.productDetailFormGroup.get('ImageUrl')?.setErrors({ invalidFileType: true });
    return;
  }

  const maxSize = 5 * 1024 * 1024; // 5 MB
  if (file.size > maxSize) {
    this.productDetailFormGroup.get('ImageUrl')?.setErrors({ fileTooLarge: true });
    return;
  }

  const reader = new FileReader();
  reader.onload = () => {
    const dataUrl = reader.result?.toString() || '';
    this.productDetailFormGroup.get('ImageUrl')?.setValue(dataUrl);
    this.editedProduct.ImageUrl = dataUrl;
  };

  reader.readAsDataURL(file);
}


    // Get product tags
  get productTags(): string[] {
    return this.selectedTags.map(x => {
      if (typeof x === 'string') {
        return x;
      }
      if (x && typeof x === 'object' && 'Name' in x) {
        return (x as { Name: string }).Name;
      }
      return '';
    }) || [];
  }
  // SKU field (separate from product ID)
  productSku: string = '';
  // Getter for SKU
  get skuValue(): string {
    return this.productSku || '';
  }
   // Tag management methods
  onTagInputChange(event: any): void {
    const value = event.target.value;
    this.newTag = value;
    // Filter available tags based on input
    if (value && value.trim() !== '') {
      this.filteredTags = this.allTags
        .filter(tag =>
          tag.toLowerCase().includes(value.toLowerCase()) &&
          !this.selectedTags.includes(tag)
        )
        .slice(0, 10); // Limit to 10 suggestions
    } else {
      this.filteredTags = [];
    }
  }
  addTag(): void {
    if (this.selectedTag && this.selectedTag.Name.trim() !== '') {
      const tag = this.selectedTag.Name.trim();
      if (!this.selectedTags.includes(tag)) {
        this.selectedTags.push(tag);
        this.selectedTag = '';
        this.filteredTags = [];
      }
    }
  }

  selectTag(event: any): void {
    const tag = event.option.value;
    if (!this.selectedTags.includes(tag)) {
      this.selectedTags.push(tag);
    }
    this.newTag = '';
    this.filteredTags = [];
  }
  removeTag(index: number): void {
    this.selectedTags.splice(index, 1);
  }
   // Stepper navigation methods
  onStepChange(event: any): void {
    // Handle step change logic if needed
  }

  getStepTitle(): string {
    if (!this.stepper) {
      return 'Edit Product Details';
    }

    switch (this.stepper.selectedIndex) {
      case 0:
        return 'Edit Product Details';
      case 1:
        return 'Edit Pricing Details';
      case 2:
        return 'Edit UOM Details';
      case 3:
        return 'Edit Availability Details';
      default:
        return 'Edit Product Details';
    }
  }

   goBack(): void {
    this.stepper.previous();
  }

  onNextStep(): void {
    this.submitted = true;
    if (this.stepper.selectedIndex === 0) {
      if (this.productDetailFormGroup.valid) {
        this.stepper.next();
      }
    } else if (this.stepper.selectedIndex === 1) {
      if (this.pricingFormGroup.valid) {
        this.stepper.next();
      }
    } else if (this.stepper.selectedIndex === 2) {
      if (this.uomFormGroup.valid) {
        this.stepper.next();
      }
    } else {
      this.stepper.next();
    }
  }

  isCurrentStepInvalid(): boolean {
  if (!this.stepper) {
    return false;
  }
  switch (this.stepper.selectedIndex){
    case 0:
      return this.submitted && this.productDetailFormGroup.invalid;
    case 1:
      return this.submitted && this.pricingFormGroup.invalid;
    case 2:
      return this.submitted && this.uomFormGroup.invalid;
    case 3:
      return false; // Availability step has no form
    default:
      return false;
  }
}
onSave(): void {
    this.submitted = true;
    // Validate all forms before saving
    if (this.productDetailFormGroup.invalid ||
        this.pricingFormGroup.invalid ||
        this.uomFormGroup.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please complete all required fields before saving.'
      });
      return;
    }

    this.loading = true;
    // Ensure we have the latest data before saving
    this.captureLatestPricingData();
    this.captureLatestUomData();
    this.captureLatestAvailabilityData();
    // Get form values
    const productDetailValues = this.productDetailFormGroup.value;
    const pricingValues = this.pricingFormGroup.value;
    const uomValues = this.uomFormGroup.value;
    // Get selected category and subcategory
    const selectedCategory: any = productDetailValues.Category;
    const selectedSubCategory: any = productDetailValues.Subcategory;
    const selectedUnit: any = uomValues.Measurement;
    const storeSelectedList: any[] = [...Array.from(this.storeSelectedSet)];
    const selectedTags = this.allTags.filter(tag => this.selectedTags.includes(tag.Name)).map(tag => tag.Id);
    // Create DTO similar to add dialog structure
    const updateProductDto = {
      Id: this.editedProduct.ProductId,
      MerchantId: this.editedProduct.MerchantId,
      StoreId: null,
      Name: productDetailValues.Name,
      ImageUrl: productDetailValues.ImageUrl,
      Description: productDetailValues.Description,
      SKU: productDetailValues.SKU,
      CategoryId: selectedCategory?.Id || this.editedProduct.CategoryId,
      SubCategoryId: selectedSubCategory?.Id || null,
      Tags: selectedTags,
      ExcludeProductFromStores: storeSelectedList.map((store: any) => store.Id),
      Items: [
        {
          Id: this.editedProduct.Id,
          CostPrice: pricingValues.CostPrice,
          SellingPrice: pricingValues.SellingPrice,
          UseVat: pricingValues.IsVatable,
          Vat: pricingValues.VatAmount,
          Quantity: uomValues.Units,
          PublishedPrice: pricingValues.DisplayedPrice?.toString().replace(',', ''),
          UnitId: selectedUnit?.value || this.editedProduct.UnitId,
        },
      ],
    };
    // Apply changes to the original product only when saving
    if (this.editedProduct && this.product) {
      Object.assign(this.product, this.editedProduct);
    }
    // Call the API to update the product with proper DTO
    this.productService.updateProduct(updateProductDto, this.messageService)
      .then((response) => {
        if (response.Success) {
          this.dialogRef.close({ action: 'save', product: this.product });
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Product updated successfully',
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.ErrorMessage || 'Failed to update product',
          });
        }
        this.loading = false;
      })
      .catch(err => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err,
        });
        throw err;
      });
  }

    onCancel(): void {
    // Close dialog without saving
    this.dialogRef.close({ action: 'cancel' });
  }

   onPricingDataChanged(pricingData: any): void {
    // Update only the edited product with pricing data - do not modify original product
    if (this.editedProduct) {
      this.editedProduct.DefaultAmount = pricingData.CostPrice;
      this.editedProduct.PublishedPrice = pricingData.SellingPrice;
    }
  }

  onUomDataChanged(uomData: any): void {
    // Update only the edited product with UOM data - do not modify original product
    if (this.editedProduct) {
      this.editedProduct.UOM = uomData.UOM;
      this.editedProduct.UnitId = uomData.UnitId;
      this.editedProduct.UnitText = uomData.UnitText;
      this.editedProduct.Units = uomData.Units;
      this.editedProduct.Quantity = uomData.Quantity;
    }
  }
  onAvailabilityDataChanged(availabilityData: any): void {
    // Update only the edited product with availability data - do not modify original product
    if (this.editedProduct) {
      this.editedProduct.ExcludedStores = availabilityData.ExcludedStores;
      this.editedProduct.ExcludedStoreIds = availabilityData.ExcludedStoreIds;
    }
  }
  private getSubCategoryFromApi(selectedCategory: any) {
    if (!selectedCategory) {
      this.subProductCategories = [];
      return;
    }
    const dto = { ParentId: selectedCategory.Id, GetAll: true, MerchantId: null };
    this.productService.getCategories(dto, this.messageService).then((data) => {
      if (Array.isArray(data)) {
        this.subProductCategories = data;
      }
    });
  }
  // ... rest of methods like onSave(), onCancel(), captureLatest... unchanged
  // (copy from your existing implementation)
 captureLatestPricingData(): void {
    // Get the latest pricing data from the pricing tab component
    if (this.pricingTabComponent) {
      const latestPricingData = this.pricingTabComponent.getCurrentPricingData();
      // Update only the edited product with the latest pricing data
      if (this.editedProduct) {
        this.editedProduct.DefaultAmount = latestPricingData.CostPrice;
        this.editedProduct.PublishedPrice = latestPricingData.SellingPrice;
      }
    }
  }
  captureLatestUomData(): void {
    // Get the latest UOM data from the UOM tab component
    if (this.uomTabComponent) {
      const latestUomData = this.uomTabComponent.getCurrentUomData();
      // Update only the edited product with the latest UOM data
      if (this.editedProduct) {
        this.editedProduct.UOM = latestUomData.UOM;
        this.editedProduct.UnitId = latestUomData.UnitId;
        this.editedProduct.UnitText = latestUomData.UnitText;
        this.editedProduct.Units = latestUomData.Units;
        this.editedProduct.Quantity = latestUomData.Quantity;
      }
    }
  }
  captureLatestAvailabilityData(): void {
    // Get the latest availability data from the availability tab component
    if (this.availabilityTabComponent) {
      const latestAvailabilityData = this.availabilityTabComponent.getCurrentAvailabilityData();
      // Update only the edited product with the latest availability data
      if (this.editedProduct) {
        this.editedProduct.ExcludedStores = latestAvailabilityData.ExcludedStores;
        this.editedProduct.ExcludedStoreIds = latestAvailabilityData.ExcludedStoreIds;
      }
    }
  }
}
